import { signal } from '@angular/core';
import type {
  MessengerLive,
  MessengerLiveEvent,
  MessengerRealtimeStatus,
  MessengerRuntimeConfig,
} from '@crm/messenger';
import {
  hubConversationId,
  normalizeMcsHubMessage,
  participantDirectionsFrom,
  type ParticipantDirection,
} from './mello-messenger-mappers';
import type {
  McsHubConnectionLike,
  McsHubConnector,
  McsSignalRSession,
} from './mello-messenger-send.transport';

type HubHandler = (...args: unknown[]) => void;

export interface McsHubListener extends McsHubConnectionLike {
  on(method: string, handler: HubHandler): void;
  onreconnecting?(handler: HubHandler): void;
  onreconnected?(handler: HubHandler): void;
  onclose?(handler: HubHandler): void;
  stop?(): Promise<void>;
}

export interface McsLiveMessagesOptions {
  readonly config: () => MessengerRuntimeConfig;
  readonly tokenProvider: () => Promise<string>;
  readonly connector: McsHubConnector;
  readonly fetchImpl?: typeof fetch;
  readonly retryDelaysMs?: readonly number[];
}

export const MCS_LIVE_EVENTS = {
  message: 'broadcastMessage',
  messageUpdate: 'broadcastMessageUpdate',
  conversationUpdate: 'broadcastConversationUpdate',
  readState: 'conversationReadStateChanged',
  conversations: 'receiveConversations',
} as const;

const DEFAULT_RETRY_DELAYS_MS = [2000, 5000, 15000, 30000, 60000];
const REPRIME_INTERVAL_MS = 10_000;

/**
 * Listens on the MCS conversation hub for messages pushed to the signed-in
 * user. Broadcasts carry no direction, so a participant directory is primed
 * with `GetConversations` and refreshed when an unknown participant appears.
 */
export class McsLiveMessages implements MessengerLive {
  private readonly state = signal<MessengerRealtimeStatus>('idle');
  readonly status = this.state.asReadonly();

  private readonly fetchImpl: typeof fetch;
  private readonly retryDelays: readonly number[];
  private directions = new Map<string, ParticipantDirection>();
  private connection: McsHubListener | null = null;
  private opening: Promise<void> | null = null;
  private onEvent: ((event: MessengerLiveEvent) => void) | null = null;
  private retryTimer: ReturnType<typeof setTimeout> | undefined;
  private attempt = 0;
  private stopped = true;
  private lastPrimedAt = 0;

  constructor(private readonly options: McsLiveMessagesOptions) {
    this.fetchImpl = options.fetchImpl ?? ((...args) => fetch(...args));
    this.retryDelays = options.retryDelaysMs ?? DEFAULT_RETRY_DELAYS_MS;
  }

  connect(onEvent: (event: MessengerLiveEvent) => void): Promise<void> {
    this.onEvent = onEvent;
    this.stopped = false;
    if (this.connection) return Promise.resolve();
    this.opening ??= this.open().finally(() => (this.opening = null));
    return this.opening;
  }

  async disconnect(): Promise<void> {
    this.stopped = true;
    clearTimeout(this.retryTimer);
    const connection = this.connection;
    this.connection = null;
    this.state.set('idle');
    await connection?.stop?.().catch(() => undefined);
  }

  private async open(): Promise<void> {
    if (this.options.config().mode !== 'remote') return;
    this.state.set('connecting');
    try {
      const connection = (await this.options.connector.connect(
        await this.fetchSession(),
        () => this.fetchSession(),
      )) as McsHubListener;
      if (this.stopped) {
        await connection.stop?.().catch(() => undefined);
        this.state.set('idle');
        return;
      }
      this.connection = connection;
      this.attach(connection);
      this.attempt = 0;
      this.state.set('connected');
      await this.prime(connection);
    } catch {
      this.connection = null;
      this.state.set('error');
      this.scheduleRetry();
    }
  }

  private attach(connection: McsHubListener): void {
    connection.on(MCS_LIVE_EVENTS.conversations, (payload) => {
      for (const [guid, direction] of participantDirectionsFrom(payload)) {
        this.directions.set(guid, direction);
      }
    });
    connection.on(MCS_LIVE_EVENTS.message, (payload) => void this.deliver(connection, payload, false));
    connection.on(MCS_LIVE_EVENTS.messageUpdate, (payload) => void this.deliver(connection, payload, true));
    for (const name of [MCS_LIVE_EVENTS.conversationUpdate, MCS_LIVE_EVENTS.readState]) {
      connection.on(name, (payload) => {
        this.onEvent?.({ kind: 'conversation', conversationId: hubConversationId(payload) });
      });
    }
    connection.onreconnecting?.(() => this.state.set('reconnecting'));
    connection.onreconnected?.(() => {
      this.state.set('connected');
      void this.prime(connection).then(() => this.onEvent?.({ kind: 'resync' }));
    });
    connection.onclose?.(() => {
      if (this.connection !== connection) return;
      this.connection = null;
      if (this.stopped) return;
      this.state.set('error');
      this.scheduleRetry();
    });
  }

  private async deliver(
    connection: McsHubListener,
    payload: unknown,
    update: boolean,
  ): Promise<void> {
    const participant = readParticipantGuid(payload);
    if (participant && !this.directions.has(participant)) await this.prime(connection);
    const mapped = normalizeMcsHubMessage(payload, this.directions);
    if (mapped) {
      this.onEvent?.({ kind: 'message', conversationId: mapped.conversationId, message: mapped.message, update });
    }
  }

  private async prime(connection: McsHubListener): Promise<void> {
    const now = Date.now();
    if (now - this.lastPrimedAt < REPRIME_INTERVAL_MS && this.directions.size) return;
    this.lastPrimedAt = now;
    try {
      await connection.invoke('GetConversations', { createNew: false, contacts: [], getAll: true });
    } catch {
      this.lastPrimedAt = 0;
    }
  }

  private scheduleRetry(): void {
    if (this.stopped) return;
    clearTimeout(this.retryTimer);
    const delay = this.retryDelays[Math.min(this.attempt, this.retryDelays.length - 1)] ?? 60000;
    this.attempt += 1;
    this.retryTimer = setTimeout(() => {
      void this.open().then(() => {
        if (this.connection) this.onEvent?.({ kind: 'resync' });
      });
    }, delay);
  }

  private async fetchSession(): Promise<McsSignalRSession> {
    const config = this.options.config();
    const token = await this.options.tokenProvider();
    if (!token) throw new Error('Sign-in is required.');
    const baseUrl = resolveSignalRApiBaseUrl(config.apiBaseUrl);
    const url = new URL(`${baseUrl}/current-user/mcs-signalr`, location.origin);
    if (config.businessUnit) url.searchParams.set('businessUnit', config.businessUnit);
    const response = await this.fetchImpl(url.toString(), {
      headers: { accept: 'application/json', authorization: `Bearer ${token}` },
    });
    if (!response.ok) throw new Error(`Session request failed (${response.status}).`);
    const body = (await response.json()) as Record<string, unknown>;
    const hubUrl = String(body['hubUrl'] ?? body['url'] ?? body['endpoint'] ?? '');
    const sessionToken = String(body['token'] ?? body['accessToken'] ?? '');
    if (!hubUrl || !sessionToken) throw new Error('The live session response was incomplete.');
    return { hubUrl, token: sessionToken };
  }
}

function resolveSignalRApiBaseUrl(rawBaseUrl: string): string {
  const trimmed = rawBaseUrl.replace(/\/+$/, '');
  if (typeof location === 'undefined') return trimmed;
  try {
    const hostUrl = new URL(location.origin);
    const isLocalDev = ['localhost', '127.0.0.1'].includes(hostUrl.hostname);
    if (!isLocalDev) return trimmed;
    const parsed = new URL(trimmed, location.origin);
    if (
      /^ldapi-[a-z0-9-]+\.loandepotdev\.works$/i.test(parsed.hostname) &&
      parsed.pathname.includes('/omni-messenger')
    ) {
      return '/api/messenger';
    }
    return trimmed;
  } catch {
    return trimmed;
  }
}

function readParticipantGuid(payload: unknown): string {
  const record = payload as Record<string, unknown> | null;
  const value = record?.['ConversationParticipantGuid'] ?? record?.['conversationParticipantGuid'];
  return typeof value === 'string' ? value.toLowerCase() : '';
}
